import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Divider, Stack, Typography, TypographyProps, Box } from "@mui/material";
import {
    CheckoutFormTypeFields,
    CheckoutProcessStep,
    useCheckoutForm,
} from "./hooks/useCheckoutForm";
import {
    ArrowLeftIcon,
    EnvelopeSimpleIcon,
    StorefrontIcon,
    CreditCardIcon,
    MapPinIcon,
    IdentificationCardIcon,
    HouseSimpleIcon,
} from "@phosphor-icons/react";
import {
    DOC_DNI_ID,
    OrderDeliveryAddressFields,
    OrderPersonalInformationFields,
    OrderPaymentOptionViewDTO,
    OrderPaymentOptionViewDTOFields,
} from "../../types/purchaseOrder/Order";
import { STYLES } from "../ShoppingCart/ShoppingCartStyles";
import { HttpCache } from "../../http/httpCache";
import { EntityWithIdAndDescription } from "../../types/baseEntities";
import { MP_LOGO_URL, PROVEEDOR_PAGO } from "./checkoutConstants";
import { formatCuit, formatAddress, formatPhoneDisplay } from "./checkoutUtils";

type PaymentMeta = {
    label: string;
    description?: string | null;
    isMercadoPago: boolean;
};

const resolvePaymentMeta = (
    paymentOptionId: string | undefined,
    paymentOptions: OrderPaymentOptionViewDTO[],
): PaymentMeta | null => {
    if (!paymentOptionId) return null;
    const selected = (paymentOptions ?? []).find(
        (o: any) => String(o?.id ?? "") === String(paymentOptionId),
    );
    if (!selected) return null;
    const providerCode = selected?.[OrderPaymentOptionViewDTOFields.ProviderCode];
    const label =
        String(selected?.[OrderPaymentOptionViewDTOFields.OptionName] ?? "").trim() || "Pago";
    const description =
        String(selected?.[OrderPaymentOptionViewDTOFields.Description] ?? "").trim() || null;
    return { label, description, isMercadoPago: providerCode === PROVEEDOR_PAGO.MERCADO_PAGO };
};

export const PaymentSection: React.FC = () => {
    const {
        methods,
        setCurrentStep,
        deliveryType,
        deliveryOptions,
        paymentOptions,
        paymentOptionId,
        getPaymentData,
        selectedPaymentProviderCode,
    } = useCheckoutForm();

    const watchPersonal = methods.watch(CheckoutFormTypeFields.PersonalInformation);
    const watchBilling = methods.watch(CheckoutFormTypeFields.DeliveryAddress);
    const watchSame = methods.watch(CheckoutFormTypeFields.SameDeliveryAddress) ?? true;
    const watchShipping = methods.watch(CheckoutFormTypeFields.ShippingAddress);

    const paymentData = useMemo(() => getPaymentData(), [getPaymentData, paymentOptionId]);

    const [afipTipos, setAfipTipos] = useState<EntityWithIdAndDescription[]>([]);
    useEffect(() => {
        HttpCache.getAfipResponsableTipos()
            .then(setAfipTipos)
            .catch(() => {});
    }, []);

    const esDNI =
        (watchPersonal?.[OrderPersonalInformationFields.DocumentoTipo] as number | undefined) ===
        DOC_DNI_ID;

    const docLabel = esDNI
        ? watchPersonal?.[OrderPersonalInformationFields.Document]
            ? `DNI ${watchPersonal[OrderPersonalInformationFields.Document]}`
            : null
        : watchPersonal?.[OrderPersonalInformationFields.Cuit]
          ? `CUIT ${formatCuit(watchPersonal[OrderPersonalInformationFields.Cuit] as string)}`
          : null;

    const nombreDisplay = esDNI
        ? `${watchPersonal?.[OrderPersonalInformationFields.FirstName] ?? ""} ${watchPersonal?.[OrderPersonalInformationFields.LastName] ?? ""}`.trim()
        : ((watchPersonal?.[OrderPersonalInformationFields.RazonSocial] as string | undefined) ??
          "");

    const afipDesc = !esDNI
        ? (afipTipos.find(
              (t) => t.id === watchPersonal?.[OrderPersonalInformationFields.AfipResponsableTipo],
          )?.descripcion ?? null)
        : null;

    const phoneDisplay = formatPhoneDisplay(
        watchPersonal?.[OrderPersonalInformationFields.AreaCode] as string,
        watchPersonal?.[OrderPersonalInformationFields.PhoneNumber] as string,
    );

    const billingAddrDisplay = formatAddress(
        watchBilling?.[OrderDeliveryAddressFields.Street] as string,
        watchBilling?.[OrderDeliveryAddressFields.StreetNumber] as string,
        watchBilling?.[OrderDeliveryAddressFields.FloorApartment] as string,
        watchBilling?.[OrderDeliveryAddressFields.LocalityDesc] as string,
        watchBilling?.[OrderDeliveryAddressFields.ZipCode] as string,
    );

    const shippingAddrDisplay = !watchSame
        ? formatAddress(
              watchShipping?.[OrderDeliveryAddressFields.Street] as string,
              watchShipping?.[OrderDeliveryAddressFields.StreetNumber] as string,
              watchShipping?.[OrderDeliveryAddressFields.FloorApartment] as string,
              watchShipping?.[OrderDeliveryAddressFields.LocalityDesc] as string,
              watchShipping?.[OrderDeliveryAddressFields.ZipCode] as string,
          )
        : null;

    const deliveryOption = useMemo(
        () =>
            deliveryOptions
                .flatMap((g) => g.options)
                .find((o) => `${o.value}` === `${deliveryType}`) ?? null,
        [deliveryType, deliveryOptions],
    );

    const paymentMeta = useMemo(
        () => resolvePaymentMeta(paymentOptionId, paymentOptions),
        [paymentOptionId, paymentOptions],
    );

    const changeBtn = (
        <Typography
            fontFamily="inherit"
            fontSize="14px"
            fontWeight={600}
            onClick={() => setCurrentStep(CheckoutProcessStep.CustomerDetails)}
            sx={{ "&:hover": { opacity: 0.6, cursor: "pointer" } }}
        >
            Cambiar
        </Typography>
    );

    return (
        <Stack mb={2} spacing={2}>
            <Box>
                <Button
                    startIcon={<ArrowLeftIcon size={16} />}
                    onClick={() => setCurrentStep(CheckoutProcessStep.CustomerDetails)}
                    size="small"
                    sx={{
                        fontFamily: "inherit",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "rgba(0,0,0,0.55)",
                        textTransform: "none",
                        pl: 0,
                        "&:hover": { background: "transparent", color: "rgba(0,0,0,0.85)" },
                    }}
                >
                    Volver al formulario
                </Button>
            </Box>

            <Alert
                severity="info"
                sx={{
                    fontFamily: "inherit",
                    fontSize: "14px",
                    backgroundColor: "rgba(127, 140, 141, .15)",
                    border: "1px solid rgba(127, 140, 141, .2)",
                    borderRadius: "10px",
                    color: "#6d6d6d",
                    "& > div > svg": { color: "#6d6d6d" },
                }}
            >
                Revisá tu información y hacé click en <strong>Realizar Pedido</strong> para
                confirmar.
            </Alert>

            <Stack
                spacing={2}
                paddingX={3.85}
                paddingY={2}
                sx={{ border: "1px solid rgba(1,1,1,.1)", borderRadius: 2 }}
            >
                {/* Email */}
                <Row Icon={EnvelopeSimpleIcon}>
                    <SubtitleLabel sx={{ wordBreak: "break-all" }}>
                        {watchPersonal?.[OrderPersonalInformationFields.Email] as string}
                    </SubtitleLabel>
                </Row>

                <Divider />

                {/* Datos personales */}
                <Row Icon={IdentificationCardIcon} action={changeBtn}>
                    <Stack spacing={0.4}>
                        <TitleLabel>Datos personales</TitleLabel>
                        {!!nombreDisplay && <SubtitleLabel>{nombreDisplay}</SubtitleLabel>}
                        {!!docLabel && <SubtitleLabel>{docLabel}</SubtitleLabel>}
                        {!!afipDesc && <SubtitleLabel>{afipDesc}</SubtitleLabel>}
                        {!!phoneDisplay && <SubtitleLabel>{phoneDisplay}</SubtitleLabel>}
                    </Stack>
                </Row>

                <Divider />

                {/* Domicilio de facturación */}
                <Row Icon={HouseSimpleIcon} action={changeBtn}>
                    <Stack spacing={0.4}>
                        <TitleLabel>Domicilio de facturación</TitleLabel>
                        <SubtitleLabel>{billingAddrDisplay ?? "No especificado"}</SubtitleLabel>
                    </Stack>
                </Row>

                {!watchSame && (
                    <>
                        <Divider />
                        <Row Icon={MapPinIcon} action={changeBtn}>
                            <Stack spacing={0.4}>
                                <TitleLabel>Domicilio de instalación</TitleLabel>
                                <SubtitleLabel>
                                    {shippingAddrDisplay ?? "No especificado"}
                                </SubtitleLabel>
                            </Stack>
                        </Row>
                    </>
                )}

                <Divider />

                {/* Opción de envío */}
                <Row Icon={StorefrontIcon} action={changeBtn}>
                    <Stack spacing={0.4}>
                        <TitleLabel>Envío</TitleLabel>
                        {deliveryOption ? (
                            <>
                                <SubtitleLabel fontWeight={600}>
                                    {deliveryOption.summary}
                                </SubtitleLabel>
                                {!!deliveryOption.description && (
                                    <SubtitleLabel>{deliveryOption.description}</SubtitleLabel>
                                )}
                            </>
                        ) : (
                            <SubtitleLabel>-</SubtitleLabel>
                        )}
                    </Stack>
                </Row>

                <Divider />

                {/* Método de pago */}
                <Row Icon={CreditCardIcon} action={changeBtn}>
                    <Stack spacing={0.4}>
                        <TitleLabel>Método de pago</TitleLabel>
                        {!paymentMeta ? (
                            <SubtitleLabel>-</SubtitleLabel>
                        ) : paymentMeta.isMercadoPago ? (
                            <Box sx={{ pt: 0.25 }}>
                                <Box
                                    component="img"
                                    src={MP_LOGO_URL}
                                    alt="Mercado Pago"
                                    loading="lazy"
                                    sx={{ height: 36, width: "auto", display: "block" }}
                                />
                            </Box>
                        ) : (
                            <>
                                <SubtitleLabel fontWeight={600}>{paymentMeta.label}</SubtitleLabel>
                                {!!paymentMeta.description &&
                                    paymentMeta.description.toLowerCase() !==
                                        paymentMeta.label.toLowerCase() && (
                                        <SubtitleLabel>{paymentMeta.description}</SubtitleLabel>
                                    )}
                            </>
                        )}

                        {selectedPaymentProviderCode === PROVEEDOR_PAGO.TARJETA && paymentData && (
                            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                {!!paymentData.tarjetaNombreTitular && (
                                    <SubtitleLabel>
                                        {paymentData.tarjetaNombreTitular}
                                    </SubtitleLabel>
                                )}
                                {!!paymentData.tarjetaNumero && (
                                    <SubtitleLabel>
                                        •••• •••• •••• {paymentData.tarjetaNumero.slice(-4)}
                                    </SubtitleLabel>
                                )}
                                {!!paymentData.tarjetaFechaVto && (
                                    <SubtitleLabel>
                                        Vto. {paymentData.tarjetaFechaVto}
                                    </SubtitleLabel>
                                )}
                            </Stack>
                        )}

                        {selectedPaymentProviderCode === PROVEEDOR_PAGO.CBU && paymentData?.cbu && (
                            <SubtitleLabel sx={{ mt: 0.5 }}>
                                •••• •••• •••• •••• {String(paymentData.cbu).slice(-6)}
                            </SubtitleLabel>
                        )}
                    </Stack>
                </Row>
            </Stack>
        </Stack>
    );
};

interface RowProps {
    Icon: React.ElementType;
    children: React.ReactNode;
    action?: React.ReactNode;
}

const Row: React.FC<RowProps> = ({ Icon, children, action }) => (
    <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ gap: 2, minWidth: 0 }}
    >
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
            <Icon
                size={22}
                color={STYLES.colors.secondary}
                style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Stack>
        {!!action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Stack>
);

const TitleLabel: React.FC<TypographyProps> = (props) => (
    <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{
            color: "#1b1b1b",
            fontSize: "13px",
            fontFamily: "inherit",
            letterSpacing: "0.01em",
        }}
        {...props}
    />
);

const SubtitleLabel: React.FC<TypographyProps> = (props) => (
    <Typography
        variant="body2"
        sx={{
            color: "#1b1b1b",
            opacity: 0.75,
            fontSize: "13px",
            fontFamily: "inherit",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
        }}
        {...props}
    />
);
